import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import cn from 'classnames';

import useWindowDimensions from 'hooks/useWindowDimensions';

import * as routes from 'pages/routes';

import logo from 'public/images/logo.png';

import * as userResource from 'resources/user/hooks';
import * as companyResource from 'resources/company/hooks';
import * as companyHelpers from 'resources/company/company.helpers';
import * as teamsResources from 'resources/teams/hooks';
import isInIframe from 'utils/isInIframe';

import Link from 'next/link';
import Button from 'UI/Button';
import useUserTabs from '../hooks/useUserTabs';

import SideBar from '../SideBar';
import DesktopSideBar from '../DesktopSideBar';

import styles from './styles.module.scss';

const MainLayout = ({ children, lastMenuTabsScreens, pageContentOffset }) => {
  const router = useRouter();
  const { isDesktopWidth } = useWindowDimensions();
  const isIframe = isInIframe();

  const { data: user } = userResource.useCurrentUser();
  const { hasAdminPermissions } = userResource.useCurrentUserPermissions();
  const { data: company } = companyResource.useCompany(user?.company_id);
  const { companyLogoUrl, companyBackground } = companyResource.useCompanyBranding();
  const { subscribe } = companyResource.useSubscribe();
  const { data: team } = teamsResources.useGetTeamById(user?.team_id, { enabled: !!user?.team_id });

  const brandingLogoUrl = useMemo(() => {
    // If user is a realtor or client, use the team logo
    if (user?.type === 'realtor' || user?.type === 'client') {
      return team?.logo_url || companyLogoUrl;
    }

    return companyLogoUrl;
  }, [team, companyLogoUrl, user]);

  const isTopBannerVisible =
    // show to admins only
    hasAdminPermissions
    // show only if the company has never had a subscription
    && companyHelpers.wasNeverSubscribed(company)
    // show only if the company is on trial or the trial has expired
    && (companyHelpers.isTrialing(company) || companyHelpers.isTrialExpired(company));
  const daysLeftOnTrial = companyHelpers.daysLeftOnTrial(company);

  const [isSubscribeLoading, setIsSubscribeLoading] = useState(false);
  const [isSideBarVisible, setIsSideBarVisible] = useState(false);

  const onSubscribe = async () => {
    setIsSubscribeLoading(true);
    await subscribe();
    setIsSubscribeLoading(false);
  };

  const onCloseSideBar = useCallback(() => {
    setIsSideBarVisible(false);
  }, []);

  const onSideBarToggle = useCallback(() => {
    setIsSideBarVisible(!isSideBarVisible);
  }, [isSideBarVisible]);

  const onTabClick = useCallback((tab) => {
    const { name: tabName, defaultPath } = tab;

    if (tabName === routes.tabs.bottomTabs.MORE) {
      onSideBarToggle();
      return;
    }

    if (lastMenuTabsScreens[tabName]) {
      if (router.asPath !== lastMenuTabsScreens[tabName]) {
        router.push(lastMenuTabsScreens[tabName]);
        return;
      }

      router.push(defaultPath);
    }
  }, [lastMenuTabsScreens, router, onSideBarToggle]);

  const { menuTabs } = useUserTabs({ user, company });

  const isBottomNavBarVisible = !isDesktopWidth
    && routes.configurations(user, company).find((item) => item.path === router.route && item.tab);

  return (
    <div className={cn(styles.mainLayoutWrapper, { [styles.isIframe]: isIframe })}>
      {isTopBannerVisible
        && (
          <div className={styles.topBanner}>
            {daysLeftOnTrial > 0 ? (
              <span>
                You have
                <b>{daysLeftOnTrial > 1 ? ` ${daysLeftOnTrial} days` : ' less than a day'}</b>
                {' '}
                remaining in your free trial.
              </span>
            ) : (
              <span>Your trial has expired.</span>
            )}
            <Button type="primary" width="auto !important" onClick={onSubscribe} isLoading={isSubscribeLoading}>
              Upgrade Now
            </Button>
          </div>
        )}

      <header
        className={cn(styles.mainLayoutHeader, { [styles.withBranding]: brandingLogoUrl })}
        style={{ backgroundColor: companyBackground }}
        aria-hidden
      >
        <Link href={routes.path.home} passHref>
          <img
            src={brandingLogoUrl || logo}
            className={cn(styles.logo, { [styles.withBranding]: brandingLogoUrl })}
            alt="logo"
          />
        </Link>
      </header>

      <main
        className={cn(styles.main, {
          [styles[pageContentOffset]]: pageContentOffset,
          [styles.withTopBanner]: isTopBannerVisible,
        })}
      >
        {children}
        <div id="bottom-portal-container" className={styles.bottomPortalContainer} />
      </main>

      {!isIframe && (
        <>
          {isBottomNavBarVisible && (
            <nav className={styles.navigation}>
              {menuTabs.map((item) => {
                const isActiveTab = item.paths.includes(router.route);

                return (
                  <div
                    key={item.name}
                    className={cn({
                      [styles.tab]: true,
                      [styles.activeTab]: isActiveTab,
                    })}
                    style={{ width: `${100 / menuTabs.length}%` }}
                    onClick={() => onTabClick(item)}
                    aria-hidden
                  >
                    {item.icon()}
                    <p>{item.name}</p>
                  </div>
                );
              })}
            </nav>
          )}

          {isDesktopWidth && (
            <DesktopSideBar isTopBannerVisible={isTopBannerVisible} />
          )}

          {!isDesktopWidth && (
            <SideBar
              isShown={isSideBarVisible}
              onClose={onCloseSideBar}
            />
          )}
        </>
      )}
    </div>
  );
};

MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
  lastMenuTabsScreens: PropTypes.shape().isRequired,
  pageContentOffset: PropTypes.string,
};

MainLayout.defaultProps = {
  pageContentOffset: 'defaultOffset',
};

export default MainLayout;
